import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Send, Loader2 } from "lucide-react";

const EASE_PANEL = [0.32, 0.72, 0, 1];

export const ContactPanel = ({ open, onClose, subject = "licenciar" }) => {
  const [form, setForm] = useState({ nome: "", email: "", mensagem: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const isDeck = subject === "deck";
  const title = isDeck ? "Receber o deck" : "Fale com o time";
  const subtitle = isDeck
    ? "Envie seus dados e mandaremos o deck completo do DóRéMigos."
    : "Nos conte sobre o que quer saber do DóRéMigos.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, subject }),
      });

      if (!res.ok) throw new Error("Erro ao enviar");

      setStatus("sent");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const handleClose = () => {
    setStatus("idle");
    setForm({ nome: "", email: "", mensagem: "" });
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleClose}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: EASE_PANEL }}
            className="fixed right-0 top-0 z-[101] flex h-full w-full max-w-lg flex-col bg-dm-cream shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-start justify-between border-b border-dm-ink/8 px-8 pb-6 pt-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-dm-guita">
                  DóRéMigos
                </p>
                <h2 className="mt-2 font-display text-3xl font-black tracking-tight text-dm-ink sm:text-4xl">
                  {title}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="mt-1 grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-dm-ink/10 text-dm-ink transition-colors hover:bg-dm-ink hover:text-dm-cream"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-8 py-8">
              {status === "idle" || status === "sending" || status === "error" ? (
                <>
                  <p className="mb-8 text-base leading-relaxed text-dm-muted">
                    {subtitle}
                  </p>

                  {status === "error" && (
                    <div className="mb-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                      Não conseguimos enviar sua mensagem. Tente novamente ou entre em contato pelo e-mail{" "}
                      <a href="mailto:albuquerque@doremigos.com.br" className="underline font-semibold">
                        albuquerque@doremigos.com.br
                      </a>
                    </div>
                  )}

                  <div className="flex flex-col gap-6">
                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-dm-ink/60">
                        Nome
                      </label>
                      <input
                        type="text"
                        placeholder="Como podemos te chamar?"
                        value={form.nome}
                        onChange={(e) => setForm({ ...form, nome: e.target.value })}
                        disabled={status === "sending"}
                        className="w-full border-b-2 border-dm-ink/10 bg-transparent py-3 text-base text-dm-ink placeholder:text-dm-muted/50 outline-none transition-colors focus:border-dm-guita disabled:opacity-50"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-dm-ink/60">
                        E-mail
                      </label>
                      <input
                        type="email"
                        placeholder="seu@email.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        disabled={status === "sending"}
                        className="w-full border-b-2 border-dm-ink/10 bg-transparent py-3 text-base text-dm-ink placeholder:text-dm-muted/50 outline-none transition-colors focus:border-dm-guita disabled:opacity-50"
                      />
                    </div>

                    {!isDeck && (
                      <div>
                        <label className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-dm-ink/60">
                          Mensagem
                        </label>
                        <textarea
                          placeholder="Sobre o que você quer conversar?"
                          rows={4}
                          value={form.mensagem}
                          onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                          disabled={status === "sending"}
                          className="w-full resize-none border-b-2 border-dm-ink/10 bg-transparent py-3 text-base text-dm-ink placeholder:text-dm-muted/50 outline-none transition-colors focus:border-dm-guita disabled:opacity-50"
                        />
                      </div>
                    )}

                    <button
                      onClick={handleSubmit}
                      disabled={!form.nome || !form.email || status === "sending"}
                      className="group mt-4 inline-flex items-center justify-center gap-3 rounded-full bg-dm-ink px-8 py-4 text-base font-bold text-dm-cream transition-all duration-300 hover:scale-[1.03] disabled:opacity-40 disabled:hover:scale-100"
                    >
                      {status === "sending" ? (
                        <>
                          Enviando...
                          <Loader2 className="h-5 w-5 animate-spin" />
                        </>
                      ) : (
                        <>
                          {isDeck ? "Solicitar deck" : "Enviar mensagem"}
                          <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center py-16 text-center"
                >
                  <div className="mb-6 grid h-20 w-20 place-items-center rounded-full bg-dm-guita/10">
                    <Send className="h-8 w-8 text-dm-guita" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-dm-ink">
                    Mensagem enviada!
                  </h3>
                  <p className="mt-3 max-w-xs text-dm-muted">
                    Recebemos sua mensagem e responderemos em breve no e-mail informado.
                  </p>
                  <button
                    onClick={handleClose}
                    className="mt-8 rounded-full border-2 border-dm-ink/15 px-6 py-3 text-sm font-semibold text-dm-ink transition-colors hover:bg-dm-ink hover:text-dm-cream"
                  >
                    Fechar
                  </button>
                </motion.div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
